import { IndexedDBService } from '../indexed-db.service';
import { ObjectStore } from './object-stores';

export abstract class ObjectStoreService<T> {
  private readonly objectStore: ObjectStore<T>;
  private readonly indexedDBService: IndexedDBService;

  protected constructor(
    objectStore: ObjectStore<T>,
    indexedDBService: IndexedDBService,
  ) {
    this.objectStore = objectStore;
    this.indexedDBService = indexedDBService;
  }

  //this function throws an error if an object with the same key exists, use replace instead
  protected async add(object: T): Promise<Event> {
    const db = await this.indexedDBService.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(
        this.objectStore.objectStoreName,
        'readwrite',
      );
      const store = transaction.objectStore(this.objectStore.objectStoreName);
      const a = store.add(object);
      a.onsuccess = (e) => resolve(e);
      a.onerror = (e) => reject(e);
    });
  }

  //returns undefined when the key is not in the database
  protected async get(key: string): Promise<T | undefined> {
    const db = await this.indexedDBService.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(
        this.objectStore.objectStoreName,
        'readonly',
      );
      const store = transaction.objectStore(this.objectStore.objectStoreName);
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  protected async delete(key: string): Promise<Event> {
    const db = await this.indexedDBService.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(
        this.objectStore.objectStoreName,
        'readwrite',
      );
      const store = transaction.objectStore(this.objectStore.objectStoreName);
      const request = store.delete(key);
      request.onsuccess = (e) => resolve(e);
      request.onerror = (e) => reject(e);
    });
  }

  protected async replace(object: T) {
    await this.delete(object[this.objectStore.keyPath] as string);
    await this.add(object);
  }

  protected async getAll(): Promise<T[]> {
    const db = await this.indexedDBService.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(
        this.objectStore.objectStoreName,
        'readonly',
      );
      const store = transaction.objectStore(this.objectStore.objectStoreName);
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result as T[]);
      request.onerror = (e) => reject(e);
    });
  }

  protected async deleteAll(): Promise<void> {
    const leads = await this.getAll();
    for (const lead of leads) {
      await this.delete(lead[this.objectStore.keyPath] as string);
    }
  }
}
